import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Button, Input, UncontrolledTooltip } from 'reactstrap';
import { toast } from 'react-toastify';
import RefreshIcon from 'mdi-react/RefreshIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';

const p = partial('SponsorViews');

const MONTH_CHOICES = [1, 3, 6, 12, 18, 24, 36];
const DEFAULT_MONTHS = 12;
// While months are being read: check every few seconds, for at most ~5 min
const POLL_EVERY_MS = 5000;
const POLL_ATTEMPTS = 60;

// The `count` months up to and including this one, oldest first (YYYY-MM)
const monthsWindow = (count) =>
  Array.from({ length: count }, (_, index) =>
    moment()
      .subtract(count - 1 - index, 'months')
      .format('YYYY-MM')
  );

const formatMonth = (month) => moment(`${month}-01`).format('MMM YY');

// Super-admins switch the club's sponsor views on or off and choose how many
// months are read; the companies service reads them nightly, and right away
// when switched on or on "Read now" (shown as progress here).
const SponsorViewsClubSettings = ({ clubId, onChanged }) => {
  // As saved, and as edited on the page
  const [saved, setSaved] = useState({
    enabled: false,
    months: DEFAULT_MONTHS,
  });
  const [enabled, setEnabled] = useState(false);
  const [months, setMonths] = useState(DEFAULT_MONTHS);
  const [readMonths, setReadMonths] = useState([]);
  const [busy, setBusy] = useState(false);
  // Set while months are being read: since when, and which months
  const [reading, setReading] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [clubs, status] = await Promise.all([
        Api.companies.sponsorViews.getClubs(),
        Api.companies.sponsorViews.getStatus(),
      ]);
      const club = (clubs || []).find(
        (item) => String(item.clubId) === String(clubId)
      );
      const current = {
        enabled: !!club?.enabled,
        months: club?.months || DEFAULT_MONTHS,
      };
      setSaved(current);
      setEnabled(current.enabled);
      setMonths(current.months);
      setReadMonths(status?.months || []);
      return status?.months || [];
    } catch {
      return null;
    }
  }, [clubId]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Follows a read until every month that wasn't final has been read again
  const followReading = (targetMonths) => {
    const since = moment().subtract(5, 'seconds');
    setReading({ since, targetMonths });
    clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      const latest = await load();
      const done = (latest || []).filter(
        (month) =>
          targetMonths.includes(month.month) &&
          moment(month.computedAt).isAfter(since)
      ).length;
      if (done >= targetMonths.length || attempts >= POLL_ATTEMPTS) {
        clearInterval(pollRef.current);
        setReading(null);
        onChanged?.();
      }
    }, POLL_EVERY_MS);
  };

  // The months a read goes over: the window's, except those already final
  const monthsToRead = (count) => {
    const final = readMonths.filter((m) => m.frozen).map((m) => m.month);
    return monthsWindow(count).filter((month) => !final.includes(month));
  };

  const save = async () => {
    setBusy(true);
    try {
      await Api.companies.sponsorViews.setClub(clubId, { enabled, months });
      toast.success(enabled ? p('saved') : p('savedOff'));
      setSaved({ enabled, months });
      onChanged?.();
      if (enabled) {
        followReading(monthsToRead(months));
      }
    } catch {
      toast.error(p('saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const readNow = async () => {
    setBusy(true);
    try {
      await Api.companies.sponsorViews.syncClub(clubId);
      toast.success(p('syncStarted'));
      followReading(monthsToRead(saved.months));
    } catch {
      toast.error(p('syncFailed'));
    } finally {
      setBusy(false);
    }
  };

  const dirty = enabled !== saved.enabled || months !== saved.months;
  const byMonth = new Map(readMonths.map((month) => [month.month, month]));
  const lastRead = readMonths.reduce(
    (latest, month) =>
      !latest || moment(month.computedAt).isAfter(latest)
        ? moment(month.computedAt)
        : latest,
    null
  );
  const readingDone = reading
    ? reading.targetMonths.filter((month) =>
        moment(byMonth.get(month)?.computedAt).isAfter(reading.since)
      ).length
    : 0;

  return (
    <section className="sponsor-views-settings">
      <div className="sponsor-views-settings__header">
        <div>
          <h4 className="sponsor-views-settings__title">
            {p('settingsTitle')}
            <span
              className={classNames('sponsor-views-settings__pill', {
                'sponsor-views-settings__pill--on': saved.enabled,
              })}
            >
              {saved.enabled ? p('statusOn') : p('statusOff')}
            </span>
          </h4>
          <p className="sponsor-views-settings__info">{p('settingsInfo')}</p>
        </div>
        <Button
          id="sponsorViewsRefresh"
          size="sm"
          outline
          className="mb-0 icon"
          disabled={busy}
          onClick={load}
          aria-label={p('refresh')}
        >
          <RefreshIcon size={16} />
        </Button>
        <UncontrolledTooltip target="sponsorViewsRefresh">
          {p('refresh')}
        </UncontrolledTooltip>
      </div>

      <div className="sponsor-views-settings__controls">
        <div className="switch-btn">
          <label
            className={classNames('switch-btn--container', {
              active: enabled,
            })}
            htmlFor="sponsorViewsEnabled"
          >
            <input
              className="switch-btn--input"
              type="checkbox"
              id="sponsorViewsEnabled"
              checked={enabled}
              disabled={busy}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="switch-btn--button" />
          </label>
          <span
            className={classNames('switch-btn--label', { active: enabled })}
          >
            {p('collect')}
          </span>
        </div>

        <label
          className="sponsor-views-settings__months mb-0"
          htmlFor="sponsorViewsMonths"
        >
          {p('readLast')}
          <Input
            id="sponsorViewsMonths"
            type="select"
            bsSize="sm"
            value={months}
            disabled={busy || !enabled}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            {/* Keeps a value set elsewhere selectable */}
            {[...new Set([...MONTH_CHOICES, months])]
              .sort((a, b) => a - b)
              .map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
          </Input>
          {p('monthsUnit')}
        </label>

        <div className="sponsor-views-settings__actions">
          {dirty && (
            <span className="sponsor-views-settings__unsaved">
              {p('unsaved')}
            </span>
          )}
          <Button
            size="sm"
            outline
            disabled={busy || !!reading || !saved.enabled || dirty}
            onClick={readNow}
          >
            {p('syncNow')}
          </Button>
          <Button
            size="sm"
            color="primary"
            disabled={busy || !dirty}
            onClick={save}
          >
            {p('save')}
          </Button>
        </div>
      </div>

      {reading && (
        <div className="sponsor-views-settings__progress" role="status">
          {p('reading', {
            done: readingDone,
            total: reading.targetMonths.length,
          })}
        </div>
      )}

      {saved.enabled && (
        <>
          <div className="sponsor-views-settings__timeline">
            {monthsWindow(saved.months).map((month) => {
              const read = byMonth.get(month);
              const state = !read
                ? 'pending'
                : read.frozen
                  ? 'final'
                  : 'updating';
              return (
                <span
                  key={month}
                  title={
                    read
                      ? p('monthReadAt', {
                          date: moment(read.computedAt).format(
                            'D MMM YYYY HH:mm'
                          ),
                        })
                      : p('monthPending')
                  }
                  className={classNames(
                    'sponsor-views-settings__month',
                    `sponsor-views-settings__month--${state}`
                  )}
                >
                  {formatMonth(month)}
                </span>
              );
            })}
          </div>
          <div className="sponsor-views-settings__legend">
            <span>
              <i className="sponsor-views-settings__month--final" />
              {p('monthFinal')}
            </span>
            <span>
              <i className="sponsor-views-settings__month--updating" />
              {p('monthUpdating')}
            </span>
            <span>
              <i className="sponsor-views-settings__month--pending" />
              {p('monthPending')}
            </span>
          </div>
        </>
      )}

      <div className="sponsor-views-settings__meta">
        {lastRead
          ? p('lastReadAgo', {
              ago: lastRead.fromNow(),
              date: lastRead.format('D MMM YYYY HH:mm'),
            })
          : p('notReadYet')}
        {' · '}
        {p('monthsToKeepInfo')}
      </div>
    </section>
  );
};

export default SponsorViewsClubSettings;
